import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { savedAsiaPayoutData } from "../../../../redux/formDataSlice";
import { fetchCNYPurposeCodes } from "../../../../redux/slice";
import { merchantBusinessKeys } from "../../../../../Settings/settingsSlice";
import { ContentWrapper, InputWrapper, Error, ButtonsWrapper } from "../../../shared/styles";
import CustomSelect from "../../../../../../components/common/CustomSelect";
import { Button } from "../../../../../../components/common/Button";
import Loader from "../../../../../../components/common/Loader";
import { IDR_DATA_SLICE_KEY, IDR_SOURCE_OF_FUNDS } from "../const";
import { mapPurposeCodes, PurposeCodeOption } from "../../../shared/helpers";
import { IndonesiaFormInputs, IndonesiaStepFormProps } from "../types";

const IndonesiaPaymentDetailsForm: React.FC<IndonesiaStepFormProps> = ({
  handleNext,
  handlePrevious,
}) => {
  const dispatch = useAppDispatch();
  const asiaPayoutData: IndonesiaFormInputs = useAppSelector(
    (state) => (state.formData as any)?.asiaPayoutData?.[IDR_DATA_SLICE_KEY] || {}
  );
  const { keys } = useAppSelector((state) => state.settings);
  const { businessId } = useAppSelector((state) => state.users);
  const { loading } = useAppSelector((state) => state?.wallets || {});

  const [purposeOptions, setPurposeOptions] = useState<PurposeCodeOption[]>([]);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid },
  } = useForm<IndonesiaFormInputs>({
    defaultValues: asiaPayoutData,
  });

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId, dispatch]);

  useEffect(() => {
    if (!keys?.data?.publicKey) return;
    dispatch(fetchCNYPurposeCodes(keys.data.publicKey)).then((action) => {
      if (fetchCNYPurposeCodes.fulfilled.match(action)) {
        setPurposeOptions(mapPurposeCodes(action.payload));
      }
    });
  }, [dispatch, keys?.data?.publicKey]);

  const onSubmit: SubmitHandler<IndonesiaFormInputs> = (data) => {
    dispatch(
      savedAsiaPayoutData({ country: IDR_DATA_SLICE_KEY, data: { ...asiaPayoutData, ...data } })
    );
    handleNext?.();
  };

  return (
    <ContentWrapper>
      <Loader isLoading={loading} />

      <InputWrapper>
        <Controller
          name="sourceOfFunds"
          control={control}
          rules={{ required: "Source of funds is required" }}
          render={({ field }) => (
            <CustomSelect
              $width="100%"
              placeholder="Source of funds"
              options={IDR_SOURCE_OF_FUNDS}
              value={field.value || undefined}
              onChange={field.onChange}
              lightBorder
            />
          )}
        />
        {errors?.sourceOfFunds?.message && (
          <Error>{String(errors.sourceOfFunds.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="purpose"
          control={control}
          rules={{ required: "Purpose is required" }}
          render={({ field }) => (
            <CustomSelect
              $width="100%"
              placeholder="Purpose"
              options={purposeOptions}
              value={field.value || undefined}
              onChange={(val) => {
                field.onChange(val);
                const selected = purposeOptions.find((option) => option.value === val);
                setValue("purposeLabel", selected?.label);
              }}
              lightBorder
            />
          )}
        />
        {errors?.purpose?.message && <Error>{String(errors.purpose.message)}</Error>}
      </InputWrapper>

      <ButtonsWrapper>
        <Button onClick={handlePrevious} theme="alternate" height="48px" width="203px">
          Back
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={!isValid}
          label="Continue"
          height="48px"
        />
      </ButtonsWrapper>
    </ContentWrapper>
  );
};

export default IndonesiaPaymentDetailsForm;
